import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useUploadFilesApiV1FilesPost } from '#/shared/api/generated/communication/theCargoCommunication'
import { getApiErrorDetail } from '#/shared/lib/api/error-handler'
import { toast } from '#/shared/ui/sonner'

/** Backend limits: at most 10 attachments, each at most 10 MB. */
const MAX_FILES = 10
const MAX_BYTES = 10 * 1024 * 1024

type MassMediaStatus = 'uploading' | 'done' | 'error'

export interface MassMediaAttachment {
  id: string
  file: File
  /** Object URL for image previews; empty string for non-image files. */
  previewUrl: string
  /** Uppercase file extension shown on the tile badge, e.g. "PNG". */
  ext: string
  isImage: boolean
  status: MassMediaStatus
  /** Public URL returned by the upload endpoint; empty until `status === 'done'`. */
  url: string
}

function extOf(name: string): string {
  const dot = name.lastIndexOf('.')
  return dot >= 0 ? name.slice(dot + 1).toUpperCase() : ''
}

function stageFile(file: File, urls: string[]): MassMediaAttachment {
  const isImage = file.type.startsWith('image/')
  const previewUrl = isImage ? URL.createObjectURL(file) : ''
  if (previewUrl) urls.push(previewUrl)
  return {
    id: crypto.randomUUID(),
    file,
    previewUrl,
    ext: extOf(file.name),
    isImage,
    status: 'uploading',
    url: '',
  }
}

interface UseMassMediaResult {
  attachments: MassMediaAttachment[]
  addFiles: (files: FileList | null) => void
  removeAttachment: (id: string) => void
  /** Public URLs of attachments that finished uploading. */
  mediaUrls: string[]
  /** True while any attachment is still uploading. */
  isUploading: boolean
  hasMedia: boolean
  isFull: boolean
}

/**
 * Stages MMS attachments for a mass send: validates count/size, uploads each
 * file to `POST /api/v1/files`, and exposes the returned public URLs to drop
 * into `media_urls`. Object URLs used for previews are revoked on removal/unmount.
 */
export function useMassMedia(): UseMassMediaResult {
  const { t } = useTranslation()
  const [attachments, setAttachments] = useState<MassMediaAttachment[]>([])
  const urlsRef = useRef<string[]>([])
  const uploadMutation = useUploadFilesApiV1FilesPost()

  useEffect(
    () => () => {
      for (const url of urlsRef.current) URL.revokeObjectURL(url)
    },
    [],
  )

  const settle = (id: string, patch: Partial<MassMediaAttachment>) =>
    setAttachments((prev) =>
      prev.map((item) => (item.id === id ? { ...item, ...patch } : item)),
    )

  const startUpload = (item: MassMediaAttachment) => {
    uploadMutation
      // The generated body types `files` as string[] (binary), but the client
      // appends each File to FormData — File is the runtime value.
      .mutateAsync({ data: { files: [item.file] as unknown as string[] } })
      .then((res) =>
        settle(item.id, { status: 'done', url: res.data.at(0)?.url ?? '' }),
      )
      .catch((error) => {
        settle(item.id, { status: 'error' })
        toast.error(
          getApiErrorDetail(error) ??
            t('massSms.media.uploadError', { name: item.file.name }),
        )
      })
  }

  const addFiles = (files: FileList | null) => {
    if (!files || files.length === 0) return
    const room = MAX_FILES - attachments.length
    if (room <= 0) {
      toast.error(t('massSms.media.tooMany', { max: MAX_FILES }))
      return
    }
    const incoming = Array.from(files)
    const sized = incoming.filter((file) => {
      if (file.size <= MAX_BYTES) return true
      toast.error(t('massSms.media.tooLarge', { name: file.name }))
      return false
    })
    if (sized.length > room)
      toast.error(t('massSms.media.tooMany', { max: MAX_FILES }))
    const staged = sized
      .slice(0, room)
      .map((file) => stageFile(file, urlsRef.current))
    if (staged.length === 0) return
    setAttachments((prev) => [...prev, ...staged])
    for (const item of staged) startUpload(item)
  }

  const removeAttachment = (id: string) =>
    setAttachments((prev) => {
      const target = prev.find((item) => item.id === id)
      if (target?.previewUrl) {
        URL.revokeObjectURL(target.previewUrl)
        urlsRef.current = urlsRef.current.filter((u) => u !== target.previewUrl)
      }
      return prev.filter((item) => item.id !== id)
    })

  const mediaUrls = attachments
    .filter((item) => item.status === 'done')
    .map((item) => item.url)

  return {
    attachments,
    addFiles,
    removeAttachment,
    mediaUrls,
    isUploading: attachments.some((item) => item.status === 'uploading'),
    hasMedia: mediaUrls.length > 0,
    isFull: attachments.length >= MAX_FILES,
  }
}
